/**
 * Utilidades generales para la aplicación
 */

/**
 * Formatea un precio en formato moneda
 */
export function formatCurrency(amount: number, currency = 'MXN'): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formatea una fecha
 */
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...options,
  }).format(dateObj);
}

/**
 * Formatea una fecha de evento sin conversión de zona horaria
 * La fecha viene de la BD en formato ISO y debe mostrarse tal cual
 * Zona horaria: Tuxtla Gutiérrez, Chiapas, México (UTC-6)
 *
 * @param dateString - Fecha en formato ISO string
 * @param options - Opciones de formato
 * @returns Fecha formateada en español
 */

// Constantes para formateo de fechas - definidas una sola vez para mejor rendimiento
// Evita recrear estos arrays en cada llamada a formatEventDate
const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const WEEKDAYS_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];
const MONTHS_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

export function formatEventDate(
  dateString: string,
  options?: {
    includeTime?: boolean;
    includeWeekday?: boolean;
    shortFormat?: boolean;
  }
): string {
  const { includeTime = false, includeWeekday = true, shortFormat = false } = options || {};

  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  const localDate = new Date(year, month, day, hours, minutes);

  const weekday = shortFormat ? WEEKDAYS_SHORT[localDate.getDay()] : WEEKDAYS[localDate.getDay()];
  const monthName = shortFormat ? MONTHS_SHORT[localDate.getMonth()] : MONTHS[localDate.getMonth()];
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  let result = '';

  if (includeWeekday) {
    result += `${weekday}, `;
  }

  result += `${day} de ${monthName} de ${year}`;

  if (includeTime) {
    result += `, ${formattedHours}:${formattedMinutes}`;
  }

  return result;
}

/**
 * Formatea solo la hora de un evento
 * @param dateString - Fecha en formato ISO string
 * @returns Hora formateada (HH:MM)
 */
export function formatEventTime(dateString: string): string {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Genera un ID de sesión único
 */
export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Obtiene o crea un ID de sesión del localStorage
 */
export function getSessionId(): string {
  const storageKey = 'eventflow_session_id';
  let sessionId = localStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}

/**
 * Clasifica estilos condicionales
 */
export function cn(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Convierte minutos a formato mm:ss
 */
export function formatTime(minutes: number): string {
  const mins = Math.floor(minutes);
  const secs = Math.floor((minutes - mins) * 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Normaliza un número de teléfono mexicano
 * - Elimina todos los caracteres no numéricos
 * - Verifica que tenga exactamente 10 dígitos
 * - Retorna solo los números (sin código de país)
 *
 * @param phone - Número de teléfono a normalizar
 * @returns Número normalizado (10 dígitos) o null si no es válido
 */
export function normalizeMexicanPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;

  // Remover todos los caracteres no numéricos
  const digitsOnly = phone.replace(/\D/g, '');

  // Si tiene más de 10 dígitos, podría incluir código de país
  // Remover código de país mexicano (+52) si está presente
  let cleaned = digitsOnly;
  if (cleaned.length > 10) {
    // Si empieza con 52 (código de México), removerlo
    if (cleaned.startsWith('52')) {
      cleaned = cleaned.substring(2);
    }
    // Si empieza con 1 (código de EEUU/Canadá), podría ser un error
    // Por ahora, solo aceptamos 10 dígitos
  }

  // Validar que tenga exactamente 10 dígitos
  if (cleaned.length !== 10) {
    return null;
  }

  // Validar que todos sean dígitos (ya deberían serlo, pero por seguridad)
  if (!/^\d{10}$/.test(cleaned)) {
    return null;
  }

  return cleaned;
}

/**
 * Valida si un número de teléfono mexicano es válido
 * @param phone - Número de teléfono a validar
 * @returns true si es válido (10 dígitos), false en caso contrario
 */
export function isValidMexicanPhone(phone: string | null | undefined): boolean {
  return normalizeMexicanPhone(phone) !== null;
}

/**
 * Formatea un identificador de asiento para mostrar al usuario
 * Reemplaza "TABLE" por "MESA" en el identificador técnico
 *
 * @param seatIdentifier - Identificador técnico del asiento (ej: "TABLE-94-10")
 * @returns Identificador formateado para el usuario (ej: "MESA-94-10")
 *
 * @example
 * formatSeatIdentifier("TABLE-94-10") // "MESA-94-10"
 * formatSeatIdentifier("M2-05") // "M2-05"
 */
export function formatSeatIdentifier(seatIdentifier: string): string {
  if (!seatIdentifier) return '';

  // Reemplazar TABLE por MESA (case-insensitive)
  return seatIdentifier.replace(/^TABLE/i, 'MESA');
}

/**
 * Convierte una fecha local a ISO string sin conversión de zona horaria
 * Útil para enviar fechas del datetime-local al backend
 *
 * @param dateString - Fecha en formato "yyyy-MM-ddTHH:mm" del input datetime-local
 * @returns Fecha en formato ISO manteniendo la hora local
 */
export function localDateToISO(dateString: string): string {
  // El input datetime-local ya da la fecha en formato local
  // Solo necesitamos asegurarnos de que se envíe correctamente
  const date = new Date(dateString);
  return date.toISOString();
}
